/*
 *   File : sip.js
 *   Author : https://evoqins.com
 *   Description : sip component
 *   Integrations : sip-list/filter-data, user/sip-list
 *   Version : 2.0.0
 */
import React, { useState, useEffect } from "react";
import { DataTableComponent } from "../../../Components/DataTable";
import { APIService } from "../../../Service";
import { Loader, EmptyScreen } from "../../../Components/Other";

export default function Sip({
  customerId,
  setFilterData,
  setFilterType,
  appliedFilters,
  id,
}) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);

 const COLUMNS = [
  {
    name: "Name",
    selector: (row) => row.name,
    wrap: true,
    width: "250px",
    cell: (row) => (
      <div className="d-flex column-gap-2 align-items-center">
        <img
          src={row.icon}
          alt={row.name}
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.target.src = require("../../../Assets/Images/avatar.jpg");
          }}
        />
        <span>{row.name}</span>
      </div>
    ),
  },
  {
    name: "Order Type",
    width: "130px",
    selector: (row) => row.order_type || "—",
  },
  {
    name: "Start Date",
    width: "130px",
    selector: (row) => row.start_date || "—",
  },
  {
    name: "SIP Date",
    width: "180px",
    selector: (row) => row.sip_date_text || "—",
  },
  {
    name: "Completed SIP",
    width: "150px",
    selector: (row) => row.completed_sip ?? 0,
  },
  {
    name: "End Date",
    width: "130px",
    selector: (row) => row.end_date || "—",
  },
  {
    name: "SIP Amount",
    width: "150px",
    selector: (row) => row.sip_amount_text || "—",
  },
  {
    name: "Mandate ID",
    width: "150px",
    selector: (row) => row.mandate_id || "—",
  },
  {
    name: "SIP Status",
    width: "150px",
    selector: (row) => row.status,
    cell: (row) => {
      const status = row.status?.toLowerCase();

      switch (status) {
        case "active":
          return (
            <p className="fw-400 fs-12 lh-20 text-medium-sea-green mb-0">
              Active
            </p>
          );
        case "paused":
          return (
            <p className="fw-400 fs-12 lh-20 text-davys-gray mb-0">
              Paused
            </p>
          );
        case "cancelled":
          return (
            <p className="fw-400 fs-12 lh-20 text-copper-red mb-0">
              Cancelled
            </p>
          );
        default:
          return <span>{row.status ?? "—"}</span>;
      }
    },
  },
];

  // On mount: fetch filter options and push them up to parent
  useEffect(() => {
    _getFilterData();
  }, []);

  // Refetch list whenever page, perPage, or appliedFilters change
  useEffect(() => {
    _getSipList();
  }, [page, perPage, appliedFilters]);

  // ─── API: sip-list/filter-data (GET) ─────────────────────────────────────
  const _getFilterData = () => {
    APIService(false, "user/sip-list/filter-data")
      .then((res) => {
        if (res?.status_code === 200 && Array.isArray(res.data)) {
          setFilterData(res.data);          
          setFilterType(1);
        }
      })
      .catch((err) => console.error("Failed to load SIP filter data:", err));
  };

  // ─── API: user/sip-list ───────────────────────────────────────────────────
  const _getSipList = () => {
    setLoader(true);
    setIsEmpty(false);
    const payload = {
      page_num: page,
      page_size: perPage,
      customer_id: id,
      ...appliedFilters,
    };

    APIService(true, "user/sip-list", payload)
      .then((res) => {
        if (res?.status_code === 200) {
          const list = res.data?.sips ?? res.data ?? [];
          const total = res.data?.total_count ?? list.length;
          setTableData(list);
          setTotalRows(total);
          setIsEmpty(list.length === 0);
        } else {
          setTableData([]);
          setIsEmpty(true);
        }
      })
      .catch((err) => {
        console.error("Failed to load SIP list:", err);
        setTableData([]);
        setIsEmpty(true);
      })
      .finally(() => setLoader(false));
  };

  return (
    <div className="pt-1 row-gap-3 d-flex flex-column rounded-16">
      {loader ? (
        <Loader />
      ) : isEmpty ? (
        <EmptyScreen message="No SIP Data Found" />
      ) : (
        <DataTableComponent
          info={true}
          columns={COLUMNS}
          data={tableData}
          pagination={true}
          paginationServer={true}
          currentPage={page}
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          onChangePage={(p) => setPage(p)}
          onChangeRowsPerPage={(newPerPage) => {
            setPerPage(newPerPage);
            setPage(1);
          }}
          className="e-table-basic"
        />
      )}
    </div>
  );
}
